import { Layers, Monitor, Printer } from "lucide-react";
import { twJoin } from "tailwind-merge";

import type { RoutingMode } from "~/components/stations/station-edit-types";
import { getRoutingText } from "~/components/stations/station-edit-types";
import { Badge } from "~/components/ui/badge";

interface StationPreviewCardProps {
  name: string;
  colorValue: { color: string; stroke: string };
  isDefault: boolean;
  hasScreen: boolean;
  pairedDevicesCount: number;
  selectedPrinterIds: string[];
  routingMode: RoutingMode;
  categoriesSelected: string[];
  itemsSelected: string[];
  statusBadge: {
    variant: "success" | "warning" | "destructive";
    label: string;
    dotClass: string;
  };
}

export function StationPreviewCard({
  name,
  colorValue,
  isDefault,
  hasScreen,
  pairedDevicesCount,
  selectedPrinterIds,
  routingMode,
  categoriesSelected,
  itemsSelected,
  statusBadge,
}: StationPreviewCardProps) {
  const deviceText = hasScreen
    ? `${pairedDevicesCount} Device${pairedDevicesCount !== 1 ? "s" : ""}`
    : "No Screen";
  const printerText =
    selectedPrinterIds.length > 0
      ? `${selectedPrinterIds.length} Printer${selectedPrinterIds.length !== 1 ? "s" : ""}`
      : "No Printer";
  const routingText = getRoutingText(
    routingMode,
    categoriesSelected.length,
    itemsSelected.length
  );

  return (
    <div
      className="flex h-44 w-48 flex-col justify-between rounded-2xl px-5 py-4"
      style={{ background: colorValue.color, stroke: colorValue.stroke }}
    >
      <div className="flex items-start justify-between">
        <h3 className="subtitle-sm text-foreground">
          {name || "Station Name"}
        </h3>
        {isDefault && (
          <Badge className="text-xs" variant="secondary">
            Default
          </Badge>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Monitor className="h-4 w-4" />
          <p className="text-sm">{deviceText}</p>
        </div>
        <div className="flex items-center gap-2">
          <Printer className="h-4 w-4" />
          <p className="text-sm">{printerText}</p>
        </div>
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4" />
          <p className="text-sm">{routingText}</p>
        </div>
      </div>

      <Badge
        className="flex w-fit items-center gap-1 px-3 py-1"
        variant={statusBadge.variant}
      >
        <div
          className={twJoin("h-1.5 w-1.5 rounded-full", statusBadge.dotClass)}
        />
        <span>{statusBadge.label}</span>
      </Badge>
    </div>
  );
}
